import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Search, X, Building2, Loader2, Clock } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

// Fix Leaflet's default icon path issues with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom Cyan Star Marker Icon with Pulsing Animation
const createStarIcon = () => {
  return L.divIcon({
    className: 'custom-star-marker',
    html: `
      <div class="star-marker-container">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" class="star-icon">
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <path fill="#22d3ee" filter="url(#glow)" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
        <div class="pulse-ring"></div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Component to update map view when location changes
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
};

// Component to handle map clicks for placing marker
const MapClickHandler = ({ onMapClick }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleClick = (e) => {
      onMapClick(e.latlng);
    };
    
    map.on('click', handleClick);
    
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);
  
  return null;
};

const BirthplaceSearchOSM = ({ 
  onLocationSelect, 
  birthDate,
  initialLat,
  initialLng,
  initialPlaceName 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(initialPlaceName ? {
    name: initialPlaceName,
    lat: initialLat,
    lng: initialLng,
    display_name: initialPlaceName
  } : null);
  const [timezoneInfo, setTimezoneInfo] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualLat, setManualLat] = useState(initialLat || '');
  const [manualLng, setManualLng] = useState(initialLng || '');
  const [mapCenter, setMapCenter] = useState(
    initialLat && initialLng 
      ? [parseFloat(initialLat), parseFloat(initialLng)]
      : [52.4891, 13.3085] // Berlin default
  );
  const [mapZoom, setMapZoom] = useState(initialLat && initialLng ? 15 : 4);
  
  const searchTimeoutRef = useRef(null);

  // Fetch timezone from backend
  const fetchTimezone = useCallback(async (lat, lng, birthDateTime) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // If no birthDateTime provided, use current time for timezone lookup
    const timestamp = birthDateTime 
      ? Math.floor(new Date(birthDateTime).getTime() / 1000)
      : Math.floor(Date.now() / 1000);
    
    try {
      const response = await axios.post(`${backendUrl}/api/get-historical-offset`, {
        latitude: lat,
        longitude: lng,
        timestamp: timestamp
      });
      
      if (response.data) {
        return {
          timeZoneId: response.data.timezone_id,
          timeZoneName: response.data.timezone_name,
          rawOffset: response.data.utc_offset_hours,
          dstOffset: response.data.dst_offset_seconds / 3600,
          totalOffset: response.data.total_offset_hours,
          isDst: response.data.dst_active
        };
      }
      return null;
    } catch (error) {
      console.error('Timezone API error:', error);
      return null;
    }
  }, []);

  // Search using Nominatim (OpenStreetMap)
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      // Prioritize hospitals and healthcare facilities
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` + 
        `format=json&q=${encodeURIComponent(query)}&` +
        `addressdetails=1&limit=10&` +
        `extratags=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'BirthZenithStarBand/1.0'
          }
        }
      );
      
      const data = await response.json();
      
      // Sort results: hospitals first, then other places
      const sortedResults = data.sort((a, b) => {
        const aIsHospital = a.type === 'hospital' || 
                            a.class === 'amenity' && a.type === 'hospital' ||
                            a.extratags?.amenity === 'hospital';
        const bIsHospital = b.type === 'hospital' || 
                            b.class === 'amenity' && b.type === 'hospital' ||
                            b.extratags?.amenity === 'hospital';
        
        if (aIsHospital && !bIsHospital) return -1;
        if (!aIsHospital && bIsHospital) return 1;
        return 0;
      });
      
      setSearchResults(sortedResults);
    } catch (error) {
      console.error('Nominatim search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 500);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Handle place selection from search results
  const handlePlaceSelect = useCallback(async (place) => {
    setIsLoading(true);
    
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    
    // Update map
    setMapCenter([lat, lng]);
    setMapZoom(16);

    // Fetch timezone for birth date
    let timezone = null;
    if (birthDate) {
      timezone = await fetchTimezone(lat, lng, birthDate);
      setTimezoneInfo(timezone);
    }

    const placeData = {
      name: place.display_name.split(',')[0], // First part is usually the name
      formatted_address: place.display_name,
      place_id: place.place_id ? String(place.place_id) : null, // Convert to string
      lat: lat,
      lng: lng,
      timezone: timezone,
      osm_type: place.type,
      osm_class: place.class
    };

    setSelectedPlace(placeData);
    setSearchQuery('');
    setSearchResults([]);
    setIsLoading(false);
    
    onLocationSelect?.(placeData);
  }, [birthDate, fetchTimezone, onLocationSelect]);

  // Handle manual coordinate input
  const handleManualSubmit = useCallback(async () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (isNaN(lat) || isNaN(lng)) return;
    
    setIsLoading(true);
    
    // Update map
    setMapCenter([lat, lng]);
    setMapZoom(14);

    // Fetch timezone
    let timezone = null;
    if (birthDate) {
      timezone = await fetchTimezone(lat, lng, birthDate);
      setTimezoneInfo(timezone);
    }

    const placeData = {
      name: `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`,
      formatted_address: `Coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      place_id: null,
      lat: lat,
      lng: lng,
      timezone: timezone
    };

    setSelectedPlace(placeData);
    setIsLoading(false);
    setShowManualInput(false);
    
    onLocationSelect?.(placeData);
  }, [manualLat, manualLng, birthDate, fetchTimezone, onLocationSelect]);

  const clearSelection = useCallback(() => {
    setSelectedPlace(null);
    setTimezoneInfo(null);
    setSearchQuery('');
    setSearchResults([]);
    setMapZoom(4);
    setMapCenter([52.4891, 13.3085]);
    onLocationSelect?.(null);
  }, [onLocationSelect]);
  
  // Handle marker drag or map click to update location
  const handleLocationUpdate = useCallback(async (latlng) => {
    const lat = latlng.lat;
    const lng = latlng.lng;
    
    setIsLoading(true);
    
    // Update map center
    setMapCenter([lat, lng]);

    // Fetch timezone for the new location
    let timezone = null;
    if (birthDate) {
      timezone = await fetchTimezone(lat, lng, birthDate);
      setTimezoneInfo(timezone);
    }

    const placeData = {
      name: `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`,
      formatted_address: `Adjusted: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      place_id: null,
      lat: lat,
      lng: lng,
      timezone: timezone
    };

    setSelectedPlace(placeData);
    setIsLoading(false);
    
    onLocationSelect?.(placeData);
  }, [birthDate, fetchTimezone, onLocationSelect]);

  // Get icon for place type
  const getPlaceIcon = (place) => {
    if (place.type === 'hospital' || place.extratags?.amenity === 'hospital') {
      return '🏥';
    }
    if (place.type === 'city' || place.type === 'town') {
      return '🏙️';
    }
    if (place.class === 'place') {
      return '📍';
    }
    return '🗺️';
  };

  return (
    <div className="space-y-4">
      {/* Inject CSS for star marker animation */}
      <style>{`
        .custom-star-marker {
          background: none;
          border: none;
        }
        .star-marker-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .star-icon {
          position: relative;
          z-index: 2;
        }
        .pulse-ring {
          position: absolute;
          width: 40px;
          height: 40px;
          border: 2px solid #22d3ee;
          border-radius: 50%;
          animation: pulse 2s ease-out infinite;
          z-index: 1;
        }
        @keyframes pulse {
          0% {
            transform: scale(0.5);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        .leaflet-container {
          background: #0a0a0f !important;
        }
      `}</style>

      {/* Search Input */}
      <div className="relative">
        <label className="label-cosmic flex items-center gap-2 mb-2">
          <Building2 className="w-3 h-3" />
          Search Birthplace
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400 z-10" />
          <input
            type="text"
            data-testid="birthplace-search"
            placeholder="Search hospital, address, or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-3 bg-black/30 border border-white/15 rounded-lg 
                     focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 
                     text-white placeholder-white/30 transition-all"
            disabled={isLoading}
          />
          {selectedPlace && (
            <button
              onClick={clearSelection}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded z-10"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        
        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="absolute w-full mt-2 bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 
                        rounded-xl shadow-2xl overflow-hidden z-[1000] max-h-80 overflow-y-auto">
            {searchResults.map((place, index) => (
              <button
                key={place.place_id || index}
                onClick={() => handlePlaceSelect(place)}
                className="w-full px-4 py-3 text-left hover:bg-cyan-500/20 transition-colors
                         border-b border-white/5 last:border-b-0"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{getPlaceIcon(place)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {place.display_name.split(',')[0]}
                    </p>
                    <p className="text-sm text-slate-400 truncate">
                      {place.display_name}
                    </p>
                    {(place.type === 'hospital' || place.extratags?.amenity === 'hospital') && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-300 
                                   text-xs rounded">
                        Hospital
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Loading indicator for search */}
        {isSearching && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2 z-10">
            <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
          </div>
        )}
        
        {/* Manual Input Toggle */}
        <button
          onClick={() => setShowManualInput(!showManualInput)}
          className="text-xs text-cyan-400 hover:text-cyan-300 mt-2 underline decoration-dotted"
        >
          {showManualInput ? 'Use search instead' : 'Enter coordinates manually'}
        </button>
      </div>

      {/* Manual Lat/Lng Input */}
      {showManualInput && (
        <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Latitude</label>
              <input
                type="number"
                step="0.0001"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                placeholder="52.4891"
                className="w-full px-3 py-2 bg-black/30 border border-white/15 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Longitude</label>
              <input
                type="number"
                step="0.0001"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                placeholder="13.3085"
                className="w-full px-3 py-2 bg-black/30 border border-white/15 rounded text-white text-sm"
              />
            </div>
          </div>
          <button
            onClick={handleManualSubmit}
            disabled={!manualLat || !manualLng}
            className="w-full py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500 
                     text-white text-sm rounded transition-colors"
          >
            Set Location
          </button>
        </div>
      )}

      {/* Map Container with CartoDB Dark Matter tiles */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-slate-400">
            📍 Click anywhere on the map or drag the marker to adjust location
          </label>
        </div>
        <div className="w-full h-48 rounded-xl overflow-hidden border border-cyan-500/20">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
          {/* CARTO's dark basemap now requires an API key and serves tiles with
              "API KEY REQUIRED" watermarked into the image — HTTP 200, valid PNG,
              visibly broken. Standard OpenStreetMap tiles need no key; the dark
              treatment is applied in CSS via a filter on the tile pane. */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          {selectedPlace && (
            <Marker 
              position={[selectedPlace.lat, selectedPlace.lng]}
              icon={createStarIcon()}
              draggable={true}
              eventHandlers={{
                dragend: (e) => {
                  const marker = e.target;
                  const position = marker.getLatLng();
                  handleLocationUpdate(position);
                }
              }}
            />
          )}
          <MapClickHandler onMapClick={handleLocationUpdate} />
          <MapUpdater center={mapCenter} zoom={mapZoom} />
        </MapContainer>
      </div>
    </div>

      {/* Selected Place Info */}
      {selectedPlace && (
        <div className="p-4 bg-gradient-to-r from-cyan-900/30 to-indigo-900/30 border border-cyan-500/30 rounded-xl">
          {isLoading ? (
            <div className="flex items-center gap-2 text-cyan-300">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Fetching location data...</span>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">{selectedPlace.name}</p>
                  {selectedPlace.formatted_address !== selectedPlace.name && (
                    <p className="text-sm text-slate-400">{selectedPlace.formatted_address}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Latitude:</span>
                  <span className="text-cyan-300 font-mono ml-2">{selectedPlace.lat.toFixed(6)}°</span>
                </div>
                <div>
                  <span className="text-slate-500">Longitude:</span>
                  <span className="text-cyan-300 font-mono ml-2">{selectedPlace.lng.toFixed(6)}°</span>
                </div>
              </div>

              {/* Timezone Info */}
              {timezoneInfo && (
                <div className="pt-3 border-t border-cyan-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-amber-300 font-medium">Historical Timezone</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-500">Zone:</span>
                      <span className="text-white ml-2">{timezoneInfo.timeZoneName}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">UTC Offset:</span>
                      <span className="text-cyan-300 font-mono ml-2">
                        {timezoneInfo.totalOffset >= 0 ? '+' : ''}{timezoneInfo.totalOffset}h
                      </span>
                    </div>
                    {timezoneInfo.isDst && (
                      <div className="col-span-2">
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded text-xs">
                          ☀️ Daylight Saving Time was active
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BirthplaceSearchOSM;
